// @flow
/* eslint-disable */
import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { Button } from 'components';
import Icon from '../../../components/Icon';

type Props = {
  boardSituation: any,
  currentStep: any,
  setCurrentStep: () => void,
  selectedCards: Array<any>,
  actionContext?: any,
  pageMode?: string,
};

export const postflopButtonBackgrounds = {
  fold: 'linear-gradient(180.36deg, #535353 0.31%, #272727 99.69%), #898989',
  lightBet: '#FF5F5F',
  darkBet: '#BB3838',
  check: 'linear-gradient(180.36deg, rgb(112, 167, 74) 0.31%, rgb(75, 123, 39) 99.69%), rgb(88, 176, 15)',
}

export const postflopActionPotFractions = {
  small: 1 / 4,
  bet: 1 / 2,
  big: 1,
  raise: 3 / 4,
}

const normalizePostflopAction = (action) => {
  if (action === 'donk' || action === 'cbet') return 'bet'
  if (action === 'checkraise') return 'raise'
  return action
}

const normalizeVisionPostflopAction = (action) => {
  const normalizedAction = normalizePostflopAction(action)
  return normalizedAction === 'big' ? 'pot' : normalizedAction
}

const getPreviousActorActionPath = (path = [], positionCount = 0) => {
  if (!path.length || positionCount < 2) return ''

  const actorIndex = path.length % positionCount
  const previousActorIndex = (actorIndex - 1 + positionCount) % positionCount

  return path
    .filter((action, index) => index % positionCount === previousActorIndex)
    .map(normalizeVisionPostflopAction)
    .join('-')
}

const multiwaySrpPositions = ['UTGvsBTNvsBB', 'BTNvsSBvsBB']
const multiwaySqueezePositions = ['BTNvsMPvsCO', 'SBvsCOvsBTN']

const pfrOopPositions = {
  SRP: ['UTGvsBTN', 'COvsBTN', 'SBvsBB'],
  '3bP': ['SBvsUTG', 'SBvsBTN', 'SBvsCOvsBTN'],
  '4bP': ['COvsBTN', 'UTGvsBTN', 'SBvsBB'],
}

const isBlindVsBlindPosition = (position = '') => (
  position === 'SBvsBB' || position === 'BBvsSB'
)

const getPostflopSizingState = (context = {}) => {
  const currentStep = Array.isArray(context.currentStep)
    ? context.currentStep
    : (typeof context.currentStep === 'string'
      ? context.currentStep.split('-').filter(Boolean)
      : ['starting'])
  const path = currentStep
    .filter((action) => action !== 'starting')
    .map(normalizePostflopAction)
  const actionHistory = Array.isArray(context.actionHistory)
    ? context.actionHistory
    : []
  const positionLabels = Array.isArray(context.positionLabels)
    ? context.positionLabels.filter(Boolean)
    : []
  const round = getPostflopRoundState(context)
  const actorIndex = round && !round.complete
    ? positionLabels.indexOf(round.actingPosition)
    : positionLabels.length
    ? path.length % positionLabels.length
    : 0
  const actingPosition = context.actingPosition
    || positionLabels[actorIndex]
    || ''
  const actingRole = positionLabels.length > 1
    && actorIndex === positionLabels.length - 1
    ? 'IP'
    : (actorIndex === 0 ? 'OOP' : 'OOP2')
  const currentPot = Number(
    actionHistory[actionHistory.length - 1]?.values?.pot
      ?? context.initialPot
      ?? 0,
  )
  const remainingStack = getRemainingStack(
    actionHistory,
    actingPosition,
    context.stackByPosition,
  )
  const structure = context.boardSuit === 'mono'
    ? 'mono'
    : context.boardType
  const pfrIsOop = typeof context.pfrIsOop === 'boolean'
    ? context.pfrIsOop
    : (String(context.tableSize).toUpperCase() === 'HU'
      ? context.positionType === '3bP'
      : (pfrOopPositions[context.positionType] || []).includes(context.position))
  const actorActions = actionHistory
    .filter((entry) => entry.pos === actingPosition)
    .map((entry) => normalizePostflopAction(entry.action))
  const previousActorActionPath = round && actionHistory.length
    ? actionHistory.filter(entry => entry.pos === actionHistory[actionHistory.length - 1].pos)
      .map(entry => normalizeVisionPostflopAction(entry.action)).join('-')
    : getPreviousActorActionPath(
    path,
    positionLabels.length,
  )

  return {
    actionHistory,
    actingPosition,
    actingRole,
    actorActions,
    currentPot,
    isHu: String(context.tableSize).toUpperCase() === 'HU',
    isLimped: context.position === 'limpCheck',
    isLimpRaiseCall: context.position === 'limpRaiseCall',
    isMultiwaySrp: multiwaySrpPositions.includes(context.position),
    isMultiwaySqueeze: multiwaySqueezePositions.includes(context.position),
    path,
    pfrIsOop,
    position: context.position,
    positionType: context.positionType,
    previousActorActionPath,
    remainingStack,
    spr: currentPot > 0 ? remainingStack / currentPot : Infinity,
    stack: Number(String(context.stackSize || '').replace('bb', '')),
    structure,
  }
}

const getSmallPotFraction = (state) => {
  if (state.isMultiwaySqueeze) return state.stack === 50 ? 0.25 : 0.33
  if (state.stack === 200 && state.positionType === '4bP') return 0.5

  if (state.stack === 50
    && state.positionType === '3bP'
    && state.position === 'BBvsSB'
    && ['unpaired', 'straight'].includes(state.structure)) {
    return state.actingRole === 'IP' ? 0.33 : 0.5
  }

  if (state.isHu
    && state.stack === 100
    && state.positionType === '4bP') {
    return state.path[state.path.length - 1] === 'check' ? 0.33 : 0.5
  }

  if (!state.isHu
    && state.positionType === '4bP'
    && !isBlindVsBlindPosition(state.position)
  ) {
    return state.stack === 200 ? 0.5 : 0.25
  }

  if (!state.isHu
    && [100, 200].includes(state.stack)
    && !state.isLimped
    && state.positionType !== '4bP') {
    if (state.stack === 200 && state.positionType === '3bP') {
      if (state.remainingStack < state.currentPot
        || (state.spr < 1
          && (state.structure === 'unpaired'
            || isBlindVsBlindPosition(state.position)))) {
        return 0.25
      }
      if (isBlindVsBlindPosition(state.position) && state.spr < 2.9) return 0.33
    }
    return 0.5
  }

  if (state.isHu
    && state.stack === 100
    && state.positionType === 'SRP'
    && state.actingRole === 'IP') {
    return 0.5
  }

  if (state.isHu && [50, 200].includes(state.stack)) {
    if (state.remainingStack < state.currentPot) return 0.25
    if (state.remainingStack < state.currentPot * 2
      || (['check', 'call'].includes(state.path[state.path.length - 1])
        && ['mono', 'paired'].includes(state.structure))) {
      return 0.33
    }
    return 0.5
  }

  return 0.33
}

const getBetPotFraction = (state, isPlo5) => {
  // Vision initializes the opening buttons separately from later action buttons.
  if (state.stack === 200 && state.path.length === 0
    && state.positionType !== 'SRP') {
    if (state.positionType === '3bP') return 0.33
    return state.isHu || isBlindVsBlindPosition(state.position) ? 0.33 : 0.5
  }
  if (state.isHu
    && state.stack === 100
    && state.positionType === '3bP'
    && (state.path.length === 0
      || (state.path.length === 1 && state.path[0] === 'check'))) {
    return state.structure === 'unpaired' ? 0.5 : 0.33
  }

  if (!state.isHu
    && state.stack === 100
    && state.positionType === '3bP'
    && ['BTNvsUTG', 'SBvsUTG', 'BTNvsCO', 'SBvsBTN', 'BBvsSB'].includes(state.position)) {
    // Vision's initial donk size is distinct from the bet offered after a check.
    const pfrIsOop = ['SBvsUTG', 'SBvsBTN'].includes(state.position)
    if (state.path.length === 0) {
      if (state.position === 'BBvsSB') return 0.5
      if (['mono', 'paired'].includes(state.structure)) return 0.33
      return pfrIsOop ? 0.33 : 0.5
    }
    if (state.path.length === 1 && state.path[0] === 'check') {
      return pfrIsOop || state.position === 'BBvsSB' ? 0.5 : 0.33
    }
  }

  if (state.stack === 50) {
    if (state.isMultiwaySrp && state.structure !== 'unpaired') return 0.33

    if (!state.isHu
      && state.positionType !== 'SRP'
      && !state.pfrIsOop
      && state.structure === 'mono'
      && !state.isMultiwaySqueeze) {
      return isBlindVsBlindPosition(state.position) ? 0.5 : 0.33
    }

    if (state.isMultiwaySqueeze) {
      return ['straight', 'unpaired'].includes(state.structure) ? 1 : 0.25
    }

    if (state.isHu) {
      if (state.structure === 'unpaired') return state.isLimped ? 0.75 : 0.66
      if (state.structure === 'straight' || state.isLimped || state.isLimpRaiseCall) {
        return 0.5
      }
      if (state.positionType !== 'SRP') return 0.25
      return 0.33
    }

    if (state.isLimped && state.structure === 'unpaired') return 0.75
    if (isPlo5 && (!isBlindVsBlindPosition(state.position) || state.path.length > 0)
      && ['mono', 'paired'].includes(state.structure)) return 0.33
    return 0.5
  }

  if (state.positionType === 'SRP') {
    if (state.structure === 'unpaired' && state.stack !== 200) {
      return state.isHu ? 0.7 : 0.75
    }

    if (state.isMultiwaySrp && ['paired', 'mono'].includes(state.structure)) {
      return 0.33
    }

    if ((state.structure === 'paired' && state.stack === 200)
      || (state.structure === 'mono'
        && (!isBlindVsBlindPosition(state.position) || state.stack === 200))) {
      if ((state.stack !== 200 && state.isHu) || state.isLimped) return 0.5
      return 0.33
    }

    if (state.structure === 'mono' && isBlindVsBlindPosition(state.position)) return 0.5

    if (isPlo5
      && !state.isHu
      && state.structure === 'paired'
      && (state.position === 'COvsBTN'
        || (isBlindVsBlindPosition(state.position) && state.stack === 200))) {
      return 0.33
    }

    if (state.stack === 200
      && ['straight', 'unpaired'].includes(state.structure)) {
      return 0.75
    }

    return 0.5
  }

  if (!state.pfrIsOop) {
    if (isBlindVsBlindPosition(state.position)
      || (state.stack === 200
        && (state.actingRole === 'IP'
          || (state.positionType === '3bP' && state.spr < 2)))) {
      if (state.stack === 200
        && (state.spr < 0.74
          || (isBlindVsBlindPosition(state.position)
            && state.actorActions.join('-') === 'check-call'))) {
        return 1
      }
      return 0.5
    }
    return 0.33
  }

  if (state.isHu && state.structure !== 'unpaired') {
    return state.stack === 200 ? 0.5 : 0.33
  }

  return state.isMultiwaySqueeze ? 0.33 : 0.5
}

const getBigPotFraction = (state) => {
  const alreadyRaised = state.path.includes('raise')

  if (state.isHu
    && state.stack === 100
    && state.positionType === '3bP'
    && !alreadyRaised) {
    return 0.75
  }

  return 1
}

const limpCheckBetResponsePaths50 = new Set([
  'bet',
  'bet-bet',
  'bet-call-bet',
  'bet-call-raise',
  'bet-raise-bet',
  'call-bet',
  'check-bet',
  'check-call-bet',
  'check-raise-bet',
  'check-raise-call-bet',
  'raise-bet',
])

const limpCheckRaiseResponsePaths50 = new Set([
  'raise',
  'check-raise',
  'check-check-raise',
  'check-call-check-raise',
  'bet-check-raise',
])

const limpCheckBetResponsePaths100 = new Set([
  'bet',
  'bet-bet',
  'bet-call-bet',
  'bet-raise-bet',
  'call-bet',
  'check-bet',
  'check-call-bet',
  'check-raise-bet',
  'check-raise-call-bet',
  'raise-bet',
  'raise-call-bet',
])

const limpCheckRaiseResponsePaths100 = new Set([
  'raise',
  'bet-call-raise',
  'bet-check-raise',
  'bet-call-check-raise',
  'call-raise',
  'check-raise',
  'check-check-raise',
  'check-call-check-raise',
  'check-raise-check-raise',
])

const getLimpedRaisePotFraction = (state, isPlo5) => {
  if (state.positionType !== 'SRP' || ![50, 100].includes(state.stack)) {
    return undefined
  }

  const actionPath = state.previousActorActionPath
  const isNonUnpaired = state.structure !== 'unpaired'
  const hasPlo5ImmediateRaiseOverride = isPlo5
    && state.stack === 50
    && !isBlindVsBlindPosition(state.position)
    && ['paired', 'mono'].includes(state.structure)
    && (actionPath === 'raise'
      || (actionPath === 'check-raise' && state.actingRole === 'IP'))

  if (hasPlo5ImmediateRaiseOverride) return 0.5

  if (state.isLimpRaiseCall) {
    if (state.stack === 50) {
      if (actionPath === 'bet') return isNonUnpaired ? 0.5 : 1
      if (actionPath === 'check-bet') return isNonUnpaired ? 0.75 : 1
      return 1
    }

    return actionPath === 'bet' ? 0.75 : 1
  }

  if (!state.isLimped) return undefined

  const betResponsePaths = state.stack === 50
    ? limpCheckBetResponsePaths50
    : limpCheckBetResponsePaths100
  const raiseResponsePaths = state.stack === 50
    ? limpCheckRaiseResponsePaths50
    : limpCheckRaiseResponsePaths100

  if (raiseResponsePaths.has(actionPath)) return isNonUnpaired ? 0.7 : 1

  if (betResponsePaths.has(actionPath)) {
    if (isNonUnpaired) return 0.66
    return state.stack === 100 ? 0.75 : 1
  }

  return 1
}

const getRaisePotFraction = (state, isPlo5) => {
  const lastAction = state.path[state.path.length - 1]
  const raiseCount = state.path.filter((action) => action === 'raise').length
  const alreadyRaised = raiseCount > 0
  const limpedRaisePotFraction = getLimpedRaisePotFraction(state, isPlo5)

  if (limpedRaisePotFraction !== undefined) return limpedRaisePotFraction

  if (state.isMultiwaySrp) {
    if (alreadyRaised) return 1
    return state.structure === 'unpaired' ? 0.75 : 0.5
  }

  if (state.isMultiwaySqueeze) return 1

  if (state.positionType === '4bP') {
    if (alreadyRaised || lastAction !== 'bet') return 1
    if (state.isHu || isBlindVsBlindPosition(state.position)) {
      return state.actingRole === 'IP' ? 0.5 : 0.33
    }
    return 1
  }

  if (state.positionType === '3bP') {
    if (state.stack === 200) {
      const blindOrHu = state.isHu || isBlindVsBlindPosition(state.position)
      if (['mono', 'paired'].includes(state.structure) && raiseCount === 1) {
        return blindOrHu ? 0.66 : 0.5
      }
      if (state.structure === 'straight' && !blindOrHu
        && ((!alreadyRaised && lastAction === 'small')
          || (isPlo5 && state.path.join('-') === 'check-small-raise-raise'))) return 0.5
    }
    if (alreadyRaised || lastAction !== 'bet') return 1
    if (state.stack === 50) return state.isHu ? 0.33 : 1
    if (state.stack === 100
      && !state.isHu
      && state.structure === 'unpaired') return 1
    if (state.stack === 200 && state.actingRole === 'OOP') return 0.33
    return 0.5
  }

  if (state.isHu) {
    if (alreadyRaised) {
      if (state.stack === 50 && state.path.join('-') === 'check-bet-raise'
        && ['paired', 'mono'].includes(state.structure)) return 0.5
      return state.stack === 100
        && raiseCount === 1
        && ['paired', 'mono', 'straight'].includes(state.structure)
        ? 0.7
        : 1
    }

    if (lastAction !== 'bet') return 1

    if (state.stack === 50) {
      if (state.isLimped) return state.structure === 'unpaired' ? 1 : 0.66
      if (state.structure === 'unpaired') return 0.7
      if (state.actingRole === 'OOP' && ['paired', 'mono'].includes(state.structure)) {
        return 0.33
      }
      return 0.5
    }

    if (state.actingRole === 'IP') {
      return ['paired', 'mono'].includes(state.structure) ? 0.33 : 1
    }

    return state.stack === 200 ? 1 : 0.66
  }

  if (state.positionType === 'SRP'
    && !alreadyRaised
    && lastAction === 'bet') {
    if (state.stack === 50) {
      if (state.isLimped) {
        return state.structure === 'unpaired' ? 1 : 0.66
      }
      if (isBlindVsBlindPosition(state.position)
        && ['paired', 'straight', 'mono'].includes(state.structure)) {
        return 0.5
      }
      if (isPlo5 && ['paired', 'mono'].includes(state.structure)) {
        return 0.33
      }
      return 1
    }

    if (state.stack === 100) {
      if (state.isLimped && state.structure !== 'unpaired') return 0.66
      return 0.75
    }
  }

  if (state.stack === 200) {
    if (state.structure === 'paired' && !isBlindVsBlindPosition(state.position)) {
      if (state.spr < 1.5) return 1
      return state.spr < 8.1 ? 0.33 : 0.5
    }
    if (state.structure === 'mono'
      || (state.structure === 'paired' && isBlindVsBlindPosition(state.position))) {
      return state.actorActions.length ? 1 : 0.33
    }
    return state.actorActions.some((action) => (
      ['bet', 'small', 'big', 'raise'].includes(action)
    )) || isBlindVsBlindPosition(state.position)
      ? 1
      : 0.75
  }

  return 1
}

export const getPostflopActionPotFraction = (action, context = {}) => {
  const normalizedAction = normalizePostflopAction(action)
  const hasSizingContext = context.tableSize
    && context.stackSize
    && context.positionType

  if (!hasSizingContext) return postflopActionPotFractions[normalizedAction] || 0

  const state = getPostflopSizingState(context)

  if (normalizedAction === 'small') return getSmallPotFraction(state)
  if (normalizedAction === 'bet') return getBetPotFraction(state, context.isPlo5)
  if (normalizedAction === 'big') return getBigPotFraction(state)
  if (normalizedAction === 'raise') return getRaisePotFraction(state, context.isPlo5)

  return postflopActionPotFractions[normalizedAction] || 0
}

const getPositionContribution = (actionHistory = [], position = '') => (
  actionHistory
    .filter((entry) => entry.pos === position)
    .reduce((sum, entry) => sum + Number(entry.values?.bet || 0), 0)
)

const getHighestContribution = (actionHistory = []) => actionHistory.reduce(
  (highest, entry) => Math.max(
    highest,
    getPositionContribution(actionHistory, entry.pos),
  ),
  0,
)

const getRemainingStack = (actionHistory, position, stackByPosition = {}) => {
  const initialStack = Number(stackByPosition[position])

  if (!Number.isFinite(initialStack)) return Infinity

  return Math.max(0, initialStack - getPositionContribution(actionHistory, position))
}

// Replay the betting round; a call or two checks do not necessarily finish a multiway pot.
export const getPostflopRoundState = (context = {}) => {
  const labels = context.positionLabels || []
  const history = (context.actionHistory || []).filter(entry => !entry.interactive)
  const path = (Array.isArray(context.currentStep) ? context.currentStep
    : String(context.currentStep || 'starting').split('-')).filter(action => action !== 'starting')
  if (labels.length < 2 || history.length !== path.length) return null
  const contributions = Object.fromEntries(labels.map(label => [label, 0]))
  const folded = new Set()
  let pending = new Set(labels)
  let highest = 0
  let lastIndex = labels.length - 1
  const remaining = label => {
    const stack = Number(context.stackByPosition?.[label])
    return Number.isFinite(stack) ? Math.max(0, stack - contributions[label]) : Infinity
  }
  history.forEach(entry => {
    const label = entry.pos
    if (!labels.includes(label)) return
    lastIndex = labels.indexOf(label)
    contributions[label] += Number(entry.values?.bet || 0)
    if (entry.action === 'fold') folded.add(label)
    if (contributions[label] > highest + 0.000001) {
      highest = contributions[label]
      pending = new Set(labels.filter(other => other !== label && !folded.has(other) && remaining(other) > 0.000001))
    } else pending.delete(label)
    labels.forEach(other => {
      if (folded.has(other) || remaining(other) <= 0.000001) pending.delete(other)
    })
  })
  const live = labels.filter(label => !folded.has(label))
  const ableToAct = live.filter(label => remaining(label) > 0.000001)
  // Once all opponents are all-in, only an outstanding call/fold decision remains.
  if (ableToAct.length <= 1) ableToAct.forEach(label => {
    if (contributions[label] >= highest - 0.000001) pending.delete(label)
  })
  const complete = live.length <= 1 || pending.size === 0
  const actingPosition = complete ? '' : labels.map((_, i) => labels[(lastIndex + i + 1) % labels.length])
    .find(label => pending.has(label))
  const amountToCall = complete ? 0 : Math.max(0, highest - contributions[actingPosition])
  return {
    complete, actingPosition, amountToCall,
    remainingStack: complete ? 0 : remaining(actingPosition),
    canRaise: !complete && remaining(actingPosition) > amountToCall + 0.000001
      && ableToAct.some(label => label !== actingPosition),
  }
}

export const filterLegalPostflopActions = (actions, context = {}) => {
  const round = getPostflopRoundState(context)
  if (!round) return actions
  if (round.complete) return []
  return actions.filter(action => {
    if (action === 'check') return round.amountToCall < 0.000001
    if (action === 'call' || action === 'fold') return round.amountToCall > 0.000001
    if (action === 'raise') return round.amountToCall > 0.000001 && round.canRaise
    if (['bet', 'small', 'big'].includes(action)) return round.amountToCall < 0.000001 && round.canRaise
    return false
  })
}

export const hasInvalidPostflopStrategy = (source = {}, context = {}) => {
  const round = getPostflopRoundState(context)
  if (!round || round.complete) return false
  const positive = getPositivePostflopActions(source) || []
  const answers = Array.isArray(source.answer) ? source.answer : []
  const actions = [...positive, ...answers].map(normalizePostflopAction)
  return filterLegalPostflopActions(actions, context).length !== actions.length
}

export const getPostflopActionAmount = (pot, action, context = {}) => {
  const normalizedAction = normalizePostflopAction(action)
  const history = Array.isArray(context.actionHistory) ? context.actionHistory : []
  const actingPosition = context.actingPosition || ''
  const committed = getPositionContribution(history, actingPosition)
  const amountToCall = Math.max(0, getHighestContribution(history) - committed)
  const remainingStack = getRemainingStack(
    history,
    actingPosition,
    context.stackByPosition,
  )
  const capAtRemainingStack = (amount) => Math.min(
    Math.max(0, amount),
    remainingStack,
  )

  if (normalizedAction === 'check' || normalizedAction === 'fold') return 0
  if (normalizedAction === 'call') return capAtRemainingStack(amountToCall)

  const fraction = getPostflopActionPotFraction(normalizedAction, context)
  if (normalizedAction === 'raise') {
    return capAtRemainingStack(
      amountToCall + ((Number(pot) + amountToCall) * fraction),
    )
  }

  return capAtRemainingStack(Number(pot) * fraction)
}

export const createPostflopActionHistoryEntry = (pot, action, pos, context = {}) => {
  const normalizedAction = normalizePostflopAction(action)
  const bet = getPostflopActionAmount(pot, normalizedAction, {
    ...context,
    actingPosition: pos,
  })

  return {
    pos,
    action: normalizedAction,
    values: {
      pot: Number(pot) + bet,
      bet,
    },
  }
}

export const getPostflopPositionBet = (actionHistory = [], label = '') => {
  const total = getPositionContribution(actionHistory, label)
  return total > 0 ? total.toFixed(1) : ''
}

// Keep chip arithmetic precise; round only the text rendered at a seat.
export const getPostflopTableAmounts = (context = {}) => {
  const history = context.actionHistory || []
  const labels = [...new Set([...(context.positionLabels || []), ...history.map(entry => entry.pos)])]
  const contributions = Object.fromEntries(labels.map(label => [label, getPositionContribution(history, label)]))
  // The grading context stays at the decision node; the table includes the answer.
  const displayContext = history.some(entry => entry.interactive) ? {
    ...context,
    currentStep: history.map(entry => entry.action),
    actionHistory: history.map(entry => ({ ...entry, interactive: false })),
  } : context
  const collected = !!getPostflopRoundState(displayContext)?.complete
  if (collected && labels.length > 1) {
    // Return any unmatched portion of the final bet, including short all-in calls.
    const ranked = labels.slice().sort((a, b) => contributions[b] - contributions[a])
    contributions[ranked[0]] = contributions[ranked[1]]
  }
  const pot = Number(context.initialPot || 0) + (collected
    ? Object.values(contributions).reduce((sum, value) => sum + value, 0) : 0)
  return { contributions, collected, pot }
}

const postflopActionLabels = {
  small: '25%',
  bet: '50%',
  big: 'Pot',
  raise: '75%',
}

const isAggressivePostflopAction = (action) => (
  action === 'small'
  || action === 'bet'
  || action === 'big'
  || action === 'raise'
)

const postflopBetActions = ['small', 'bet', 'big']

const postflopFrequencyActions = [
  'fold',
  'check',
  'call',
  'small',
  'bet',
  'raise',
  'big',
]

const getPositivePostflopActions = (source = {}) => {
  const hasFrequencyData = postflopFrequencyActions.some((action) => (
    Object.prototype.hasOwnProperty.call(source, action)
  ))

  if (!hasFrequencyData) return null

  return postflopFrequencyActions.filter((action) => Number(source[action]) > 0)
}

const passiveActionOrder = {
  fold: 0,
  check: 1,
  call: 1,
}

export const sortPostflopActions = (actions = [], context = {}) => (
  [...new Set(actions)].sort((a, b) => {
    const normalizedA = normalizePostflopAction(a)
    const normalizedB = normalizePostflopAction(b)
    const score = (action) => {
      if (Object.prototype.hasOwnProperty.call(passiveActionOrder, action)) {
        return passiveActionOrder[action]
      }

      const fraction = getPostflopActionPotFraction(action, context)
      return fraction ? 10 + fraction : 20
    }

    return score(normalizedA) - score(normalizedB)
  })
)

export const getPostflopActionShortcut = (action, actions = [], context = {}) => {
  const normalizedAction = normalizePostflopAction(action)

  if (normalizedAction === 'fold') return '1'
  if (normalizedAction === 'check' || normalizedAction === 'call') return '2'
  if (normalizedAction === 'big') return '4'
  if (['small', 'bet', 'raise'].includes(normalizedAction)) return '3'
  return ''
}

const compareArrays = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const formatSituation = (boardSituation = []) => boardSituation.map((obj) => ({
  ...obj,
  formated_prev_actions: obj.previous_actions.split('-'),
}));

const filterPostflopActionsByFrequency = (
  actions = [],
  boardSituation = [],
  currentStep = ['starting'],
) => {
  const actionPath = Array.isArray(currentStep) ? currentStep.join('-') : currentStep
  const currentSituation = boardSituation.find((item) => (
    item?.previous_actions === actionPath
  ))
  const positiveActions = getPositivePostflopActions(currentSituation)

  if (positiveActions === null) return actions

  const availableActions = new Set(positiveActions)

  return actions.filter((action) => (
    availableActions.has(normalizePostflopAction(action))
  ))
}

export const getPostflopActionBackground = (value, context = {}) => {
  const fraction = getPostflopActionPotFraction(value, context)

  if (fraction) {
    return fraction <= 1 / 2
      ? postflopButtonBackgrounds.lightBet
      : postflopButtonBackgrounds.darkBet
  }

  switch (value) {
    case 'fold':
      return postflopButtonBackgrounds.fold
    case 'check':
      return postflopButtonBackgrounds.check
    default:
      return postflopButtonBackgrounds.check
  }
}

export const formatPostflopAction = (word, context = {}) => {
  const fraction = getPostflopActionPotFraction(word, context)

  if (fraction === 1) return 'Pot'
  if (fraction) return `${Math.round(fraction * 100)}%`
  if (postflopActionLabels[word]) return postflopActionLabels[word]
  return word.charAt(0).toUpperCase() + word.slice(1)
}

const getStoredPostflopActions = (
  boardSituation = [],
  currentStep = ['starting'],
  finalReached = false,
  filterByFrequency = true,
) => {
  if (finalReached) return [];

  const formatedSituation = formatSituation(boardSituation);

  if (currentStep[currentStep.length - 1] === 'starting') {
    const actions = formatedSituation
      .filter((el) => el.formated_prev_actions.length === 1 && el.formated_prev_actions[0] !== 'starting')
      .map((el) => el.formated_prev_actions[0]);

    const availableActions = filterByFrequency
      ? filterPostflopActionsByFrequency(actions, boardSituation, currentStep)
      : actions

    return sortPostflopActions(availableActions, { currentStep });
  }

  const nextActions = formatedSituation
    .filter((el) => (
      el.formated_prev_actions.length === (currentStep.length + 1)
      && compareArrays(currentStep, el.formated_prev_actions.slice(0, -1))
    ))
    .map((el) => el.formated_prev_actions[el.formated_prev_actions.length - 1]);

  if (nextActions.length === 0) {
    const currentSituation = boardSituation.find((item) => (
      item.previous_actions === currentStep.join('-')
    ));
    const positiveActions = getPositivePostflopActions(currentSituation) || [];
    const previousAction = currentStep[currentStep.length - 1];
    // Terminal responses have frequencies at this node but no child situation.
    const terminalActions = isAggressivePostflopAction(previousAction)
      ? ['fold', 'call']
      : previousAction === 'check' ? ['check'] : [];

    if (!terminalActions.some((action) => positiveActions.includes(action))) {
      return [];
    }

    return filterByFrequency
      ? terminalActions.filter((action) => positiveActions.includes(action))
      : terminalActions;
  }

  let showCheck = true;
  let showCall = true;
  let showFold = true;

  nextActions.forEach((word) => {
    if (word === 'check') showCheck = false;
    if (word === 'call') showCall = false;
    if (word === 'fold') showFold = false;
  });

  const actions = [];

  if (currentStep[currentStep.length - 1] === 'check' && !finalReached && showCheck) {
    actions.push('check');
  }

  if (isAggressivePostflopAction(currentStep[currentStep.length - 1])
    && !finalReached && showCall) {
    actions.push('call');
  }

  if (isAggressivePostflopAction(currentStep[currentStep.length - 1])
    && !finalReached && showFold) {
    actions.push('fold');
  }

  const availableActions = actions.concat(nextActions)
  const filteredActions = filterByFrequency
    ? filterPostflopActionsByFrequency(
      availableActions,
      boardSituation,
      currentStep,
    )
    : availableActions

  return sortPostflopActions(filteredActions, { currentStep });
};

export const getAvailablePostflopActions = (
  boardSituation = [], currentStep = ['starting'], finalReached = false,
  filterByFrequency = true, context = {},
) => {
  if (finalReached) return []
  const currentContext = { ...context, currentStep }
  let actions = getStoredPostflopActions(boardSituation, currentStep, false, filterByFrequency)
  const source = boardSituation.find(item => item.previous_actions === currentStep.join('-'))
  if (!source) return []
  // A missing child must not erase a positive action from its parent strategy.
  actions = [...actions, ...(getPositivePostflopActions(source) || []).filter(isAggressivePostflopAction)]
  if (getPostflopRoundState(currentContext) && source) {
    const invalid = hasInvalidPostflopStrategy(source, currentContext)
    const passive = ['check', 'call', 'fold'].filter(action => invalid || !filterByFrequency || Number(source[action]) > 0)
    actions = [...actions, ...passive]
  }
  return sortPostflopActions(filterLegalPostflopActions(actions, currentContext), currentContext)
}

const getPostflopNextRoundState = (currentStep, action, context = {}) => {
  const currentContext = { ...context, currentStep }
  const round = getPostflopRoundState(currentContext)
  if (!round || round.complete) return null
  const path = currentStep.filter(item => item !== 'starting').concat(action)
  const history = context.actionHistory || []
  const pot = history[history.length - 1]?.values?.pot ?? context.initialPot ?? 0
  const entry = createPostflopActionHistoryEntry(pot, action, round.actingPosition, currentContext)
  return getPostflopRoundState({ ...context, currentStep: path, actionHistory: [...history, entry] })
}

export const isPostflopTerminalAction = (boardSituation, currentStep, action, context = {}) => {
  const nextRound = getPostflopNextRoundState(currentStep, action, context)
  if (nextRound) return nextRound.complete
  // Without a complete replay context, a stored child still proves play can continue.
  const path = currentStep.filter(item => item !== 'starting').concat(action).join('-')
  return !isAggressivePostflopAction(action)
    && !boardSituation.some(row => row.previous_actions === path)
}

export const hasPostflopContinuation = (boardSituation, currentStep, action, context = {}) => {
  const path = currentStep.filter(item => item !== 'starting').concat(action)
  if (boardSituation.some(row => row.previous_actions === path.join('-'))) return true
  const round = getPostflopRoundState({ ...context, currentStep })
  if (!round) return !isAggressivePostflopAction(action)
  if (round.complete) return false
  return isPostflopTerminalAction(boardSituation, currentStep, action, context)
}

export const isPostflopFinalDecision = (boardSituation, currentStep, context = {}, filterByFrequency = false) => {
  const currentContext = { ...context, currentStep }
  const round = getPostflopRoundState(currentContext)
  const source = boardSituation.find(row => row.previous_actions === currentStep.join('-'))
  if (!source || !round || round.complete || hasInvalidPostflopStrategy(source, currentContext)) return false
  // Use the choices available in this mode; missing positive branches still count as continuations.
  const allActions = getAvailablePostflopActions(boardSituation, currentStep, false, filterByFrequency, currentContext)
  return allActions.length > 0 && allActions.every(action => isPostflopTerminalAction(
    boardSituation, currentStep, action, currentContext,
  ))
}

export const getInteractivePostflopActions = (
  combo = {},
  currentStep = ['starting'],
  fallbackActions = [],
  showAllLegalActions = false,
) => {
  if (showAllLegalActions) {
    return sortPostflopActions(fallbackActions, { currentStep })
  }

  const positiveActions = getPositivePostflopActions(combo)

  if (positiveActions !== null) {
    return sortPostflopActions(positiveActions, { currentStep })
  }

  const answers = Array.isArray(combo?.answer) ? combo.answer : []
  const previousAction = currentStep[currentStep.length - 1]
  const allKnownActions = [...fallbackActions, ...answers]
  const isCheckBetDecision = previousAction === 'starting'
    || previousAction === 'check'
    || allKnownActions.some((action) => action === 'check' || postflopBetActions.includes(action))

  if (!isCheckBetDecision) {
    return sortPostflopActions(
      fallbackActions.length > 0 ? fallbackActions : ['call', 'fold', 'raise'],
      { currentStep },
    )
  }

  const hasExplicitComboSizing = ['small', 'big'].some((action) => (
    answers.includes(action) || Number(combo?.[action]) > 0
  ))
  const availableBetActions = postflopBetActions.filter((action) => (
    (action !== 'bet' || !hasExplicitComboSizing
      || answers.includes(action) || Number(combo?.[action]) > 0)
    && (fallbackActions.includes(action)
      || answers.includes(action)
      || Number(combo?.[action]) > 0)
  ))

  return sortPostflopActions(
    ['check', ...(availableBetActions.length > 0 ? availableBetActions : ['bet'])],
    { currentStep },
  )
}

const StepsHandler = ({
  boardSituation,
  currentStep,
  setCurrentStep,
  selectedCards,
  actionContext = {},
  pageMode = 'Study',
}: Props) => {

  const currentActionContext = { ...actionContext, currentStep }
  // This row navigates Study and Train setup; active training answers render separately.
  const availableActions = sortPostflopActions(getAvailablePostflopActions(
    boardSituation,
    currentStep,
    false,
    pageMode === 'Study',
    currentActionContext,
  ), currentActionContext)
  const canGoBack = currentStep.length > 1 || currentStep[0] !== 'starting'
  const finalDecision = isPostflopFinalDecision(boardSituation, currentStep, currentActionContext, pageMode === 'Study')
  const earlyEndingActions = finalDecision ? [] : availableActions.filter(action => isPostflopTerminalAction(
    boardSituation, currentStep, action, currentActionContext,
  ))
  const unavailableActions = availableActions.filter(action => !hasPostflopContinuation(
    boardSituation, currentStep, action, currentActionContext,
  ))
  const disabledActions = [...earlyEndingActions, ...unavailableActions]
  const handleBackClick = () => {
    if (!canGoBack) return
    setCurrentStep(currentStep.length === 1 ? ['starting'] : currentStep.slice(0, -1), true)
  }
  const handleActionClick = (action) => {
    if (disabledActions.includes(action)) return
    const previousAction = currentStep[currentStep.length - 1]
    const nextStep = previousAction === 'starting'
      ? [action]
      : currentStep.concat(action)

    setCurrentStep(nextStep)

  }

  useEffect(() => {
    if (pageMode !== 'Study' && pageMode !== 'Training') return undefined

    const handleNavigationShortcut = (event) => {
      const target = event.target
      if (
        event.repeat
        || target?.matches?.('input, textarea, select, [contenteditable="true"]')
        || event.ctrlKey
        || event.altKey
        || event.metaKey
      ) return

      if (event.key === '0' && canGoBack) {
        event.preventDefault()
        handleBackClick()
        return
      }

      const action = availableActions.find((item) => (
        getPostflopActionShortcut(item, availableActions, currentActionContext) === event.key
      ))

      if (!action) return
      event.preventDefault()
      handleActionClick(action)
    }

    window.addEventListener('keydown', handleNavigationShortcut)
    return () => window.removeEventListener('keydown', handleNavigationShortcut)
  })

  const buildButtons = () => availableActions.map((action) => {
    const shortcut = getPostflopActionShortcut(
      action,
      availableActions,
      currentActionContext,
    )

    return (
      <div className={styles.mh5} key={action}>
        <Button
          className={styles.actionButton}
          disabled={disabledActions.includes(action)}
          customBackground={getPostflopActionBackground(
            action,
            currentActionContext,
          )}
          onClick={() => handleActionClick(action)}
        >
          <span className={styles.actionButtonContent}>
            <span>{formatPostflopAction(action, currentActionContext)}</span>
            {shortcut ? (
              <span className={styles.actionShortcut}>{shortcut}</span>
            ) : null}
          </span>
        </Button>
      </div>
    )
  })

  const buildBackButton = () => {
    if (!canGoBack) return null

    return (
      <button
        type="button"
        onClick={handleBackClick}
        className={styles.backButton}
        aria-label="Go back"
        aria-keyshortcuts="0"
        title="Go back (0)"
      >
        <Icon icon="undo" className={styles.backIcon} />
        <span className={styles.actionShortcut} aria-hidden="true">0</span>
      </button>
    )
  }

  return (
    <div>
      <div className={styles.flexWrapper}>
        {buildBackButton()}
        {buildButtons()}
      </div>
      {unavailableActions.length > 0 && (
        <p className={styles.unavailableMessage} role="status">
          {unavailableActions.map(action => formatPostflopAction(action, currentActionContext)).join(', ')}: continuation data is missing for this board. Choose another action or board.
        </p>
      )}
    </div>
  )
};

export default StepsHandler;
